import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, getToken, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Search, Eye, RefreshCw } from "lucide-react";

const STATUSES = ["new", "contacted", "resolved"];

export default function LeadsAdmin() {
  const qc = useQueryClient();
  const { data: leads = [], refetch, isFetching } = useQuery({
    queryKey: ["admin-leads"],
    queryFn: async () => (await api.get("/admin/leads")).data,
    refetchInterval: 20000,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });
  const [q, setQ] = useState("");
  const [view, setView] = useState(null);

  const update = useMutation({
    mutationFn: async ({ id, status }) => api.patch(`/admin/leads/${id}`, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-leads"] }),
  });

  const filtered = leads.filter((l) => (l.full_name + l.email + l.interest).toLowerCase().includes(q.toLowerCase()));

  const exportCsv = async () => {
    const res = await fetch(`${API}/admin/leads/export`, { headers: { Authorization: `Bearer ${getToken()}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "leads.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div><h1 className="font-heading text-2xl font-bold text-ink">Leads</h1><p className="mt-1 text-sm text-subtle">Contact enquiries submitted via the website.</p></div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => refetch()} disabled={isFetching} data-testid="leads-refresh"><RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="leads-export"><Download className="h-4 w-4" /> Export CSV</Button>
        </div>
      </div>

      <div className="relative mt-6 max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search leads..." className="pl-9" data-testid="leads-search" />
      </div>

      <div className="mt-4 overflow-hidden rounded-xl border border-hair bg-white shadow-sm">
        <Table>
          <TableHeader><TableRow className="bg-canvas"><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Interest</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-subtle">No leads yet.</TableCell></TableRow>}
            {filtered.map((l) => (
              <TableRow key={l.id} data-testid={`lead-row-${l.id}`}>
                <TableCell className="font-medium text-ink">{l.full_name}</TableCell>
                <TableCell className="text-sm text-subtle">{l.email}</TableCell>
                <TableCell className="text-sm">{l.interest}</TableCell>
                <TableCell>
                  <Select value={l.status} onValueChange={(v) => update.mutate({ id: l.id, status: v })}>
                    <SelectTrigger className="h-8 w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </TableCell>
                <TableCell><Button size="icon" variant="ghost" onClick={() => setView(l)}><Eye className="h-4 w-4" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!view} onOpenChange={() => setView(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Enquiry Details</DialogTitle></DialogHeader>
          {view && (
            <div className="space-y-2 text-sm">
              <p><span className="text-subtle">Name:</span> {view.full_name}</p>
              <p><span className="text-subtle">Email:</span> {view.email}</p>
              <p><span className="text-subtle">Phone:</span> {view.phone || "—"}</p>
              <p><span className="text-subtle">Country:</span> {view.country || "—"}</p>
              <p><span className="text-subtle">Interest:</span> {view.interest}</p>
              <p><span className="text-subtle">Subject:</span> {view.subject || "—"}</p>
              <p className="pt-2 text-subtle">Message:</p>
              <p className="rounded-lg bg-canvas p-3">{view.message}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
